import React, { useState } from 'react';
import { Button, Space } from 'antd';
import { Drawer } from '@common/components';
import { useMutation, useNavigate } from '@common/hooks';
import { CreateWorkforceGroup } from '@scheduling/graphql/mutations/workforce-group';
import {
  FteAttainmentMethod,
  FteAttainmentPeriod,
} from '@common/graphql/schema';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  convertToType,
  resetUtilizationOnUnrestricted,
} from '@scheduling/services/workforce-group-utils';
import { WorkforceGroupDrawerBaseForm } from '@scheduling/components/admin-workforce-group-form/workforce-group-drawer-base-form';

export type CreateWorkforceGroupInputData = {
  name: string;
  gateOpenShiftsVisibility: boolean;
  openShiftsVisibilityDelayHoursNumber: string;
  openShiftsVisibilityDelayHoursType: string;
  gateEarliestAdvanceTime: boolean;
  earliestAdvanceTimeNumber: string;
  earliestAdvanceTimeType: string;
  weeklyUtilization: [number, number];
  consecutiveHoursLimit: number;
  consecutiveHoursBridgeTolerance: number;
  consecutiveHoursResetPeriod: number;
  fteAttainmentEnabled: boolean;
  fteAttainmentMethod?: FteAttainmentMethod;
  fteAttainmentPeriod?: FteAttainmentPeriod;
  fteFixedHours?: number;
};

const inputDataDefault: CreateWorkforceGroupInputData = {
  name: '',
  gateOpenShiftsVisibility: false,
  weeklyUtilization: [0, 168],
  gateEarliestAdvanceTime: false,
  earliestAdvanceTimeNumber: '0',
  earliestAdvanceTimeType: 'days',
  openShiftsVisibilityDelayHoursNumber: '0',
  openShiftsVisibilityDelayHoursType: 'days',
  consecutiveHoursLimit: 0,
  consecutiveHoursBridgeTolerance: 0,
  consecutiveHoursResetPeriod: 0,
  fteAttainmentEnabled: false,
};

export function WorkforceGroupCreateDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();

  const [inputData, setInputData] = useState(inputDataDefault);
  const [createWorkforceGroup, { loading }] = useMutation(
    CreateWorkforceGroup,
    {
      onCompleted: (data) => {
        if (
          data?.createWorkforceGroup?.errors &&
          data?.createWorkforceGroup.errors.length > 0
        ) {
          createMessage(
            'error',
            `Failed to update the group. ${data?.createWorkforceGroup?.errors?.[0]?.fullErrors[0]}.`
          );
          return;
        }
        createMessage('success', 'Group successfully saved');
        setInputData(inputDataDefault);
        navigate('/works/admin/groups');
      },
      onError: () => {
        createMessage(
          'error',
          "Group couldn't be saved at this time. Try again later or contact support."
        );
      },
      refetchQueries: ['WorkforceGroupsQuery'],
    }
  );

  const onUpdateInputData = (
    key: string,
    value: string | boolean | number | [number, number]
  ) => {
    setInputData((prev) =>
      resetUtilizationOnUnrestricted(prev, key, value, inputDataDefault)
    );
  };

  const onSave = () => {
    const data = {
      name: inputData.name,
      consecutiveHoursLimit: inputData.consecutiveHoursLimit,
      consecutiveHoursBridgeTolerance:
        inputData.consecutiveHoursBridgeTolerance,
      consecutiveHoursResetPeriod: inputData.consecutiveHoursResetPeriod,
      weeklyMaximumHours: inputData.weeklyUtilization[1],
      weeklyMinimumHours: inputData.weeklyUtilization[0],
      gateEarliestAdvanceTime: inputData.gateEarliestAdvanceTime,
      earliestAdvanceTime: convertToType(
        inputData.earliestAdvanceTimeNumber,
        inputData.earliestAdvanceTimeType as 'days' | 'hours',
        'hours'
      ),
      earliestAdvanceTimeFormat: inputData.earliestAdvanceTimeType,
      gateOpenShiftsVisibility: inputData.gateOpenShiftsVisibility,
      openShiftsVisibilityDelayHours: convertToType(
        inputData.openShiftsVisibilityDelayHoursNumber,
        inputData.openShiftsVisibilityDelayHoursType as 'days' | 'hours',
        'hours'
      ),
      openShiftsVisibilityDelayHoursFormat:
        inputData.openShiftsVisibilityDelayHoursType,
      fteAttainmentEnabled: inputData.fteAttainmentEnabled,
      fteAttainmentMethod: inputData.fteAttainmentMethod,
      fteAttainmentPeriod: inputData.fteAttainmentPeriod,
      fteFixedHours: inputData.fteFixedHours,
    };

    createWorkforceGroup({
      variables: {
        input: data,
      },
    });
  };

  const onCloseDrawer = () => {
    onClose();
    setInputData(inputDataDefault);
  };

  const footer = (
    <Space>
      <Button size="large" onClick={onCloseDrawer}>
        Cancel
      </Button>
      <Button
        type="primary"
        size="large"
        onClick={() => onSave()}
        loading={loading}
      >
        Add Group
      </Button>
    </Space>
  );

  return (
    <Drawer
      open={open}
      className="admin-wf-group-create-drawer"
      title="Add Group"
      footer={footer}
      onClose={onCloseDrawer}
    >
      <WorkforceGroupDrawerBaseForm
        onUpdateInputData={onUpdateInputData}
        inputData={inputData}
      />
    </Drawer>
  );
}
